import API_ROUTES from "../constants/api";
import HttpUtil from "../http-handler/http-util";

export default class GeneralDisclosureService {

  static getDisclosures(forSuperAdmin = false) {
    if (forSuperAdmin) {
      return HttpUtil.get(API_ROUTES.SUPER_ADMIN_GD.GET_LIST);
    } else {
      return HttpUtil.get(API_ROUTES.GENERAL_DISCLOSURE.GET_LIST);
    }    
  }

  static createDisclosure(params, forSuperAdmin = false) {
    if (forSuperAdmin) {
      return HttpUtil.post(API_ROUTES.SUPER_ADMIN_GD.CREATE, params);
    } else {
      return HttpUtil.post(API_ROUTES.GENERAL_DISCLOSURE.CREATE, params);
    }
  }

  static updateDisclosure(disclosureId, params, forSuperAdmin = false) {
    if (forSuperAdmin) {
      return HttpUtil.put(API_ROUTES.SUPER_ADMIN_GD.UPDATE.replace('<disclosureId>', disclosureId), params);
    } else {
      return HttpUtil.put(API_ROUTES.GENERAL_DISCLOSURE.UPDATE.replace('<disclosureId>', disclosureId), params);
    }
  }

  static getDisclosure(disclosureId, fullyLoad = true, forSuperAdmin = false) {
    if (forSuperAdmin) {
      return HttpUtil.get(API_ROUTES.SUPER_ADMIN_GD.GET
        .replace('<disclosureId>', disclosureId)
        .replace('<fullyLoad>', fullyLoad)
      );
    } else {
      return HttpUtil.get(API_ROUTES.GENERAL_DISCLOSURE.GET
        .replace('<disclosureId>', disclosureId)
        .replace('<fullyLoad>', fullyLoad)
      );

    }
  }

  static getQuestion(disclosureId, questionId) {
    return HttpUtil.get(API_ROUTES.GENERAL_DISCLOSURE.GET_QUESTION
      .replace('<disclosureId>', disclosureId)
      .replace('<questionId>', questionId)
    );
  }

  static addQuestion(disclosureId, payload) {
    return HttpUtil.post(API_ROUTES.GENERAL_DISCLOSURE.ADD_QUESTION
      .replace('<disclosureId>', disclosureId), payload
      );
  }

  static updateQuestion(disclosureId, questionId, payload) {
    return HttpUtil.put(API_ROUTES.GENERAL_DISCLOSURE.UPDATE_QUESTION
      .replace('<disclosureId>', disclosureId)
      .replace('<questionId>', questionId), payload
    );
  }

  static deleteQuestion(disclosureId, questionId, forSuperAdmin = false) {
    if (forSuperAdmin) {
      return HttpUtil.delete(API_ROUTES.SUPER_ADMIN_GD.DELETE_QUESTION
        .replace('<disclosureId>', disclosureId)
        .replace('<questionId>', questionId)
      );

    } else {
      return HttpUtil.delete(API_ROUTES.GENERAL_DISCLOSURE.DELETE_QUESTION
        .replace('<disclosureId>', disclosureId)
        .replace('<questionId>', questionId)
      );
    }
  }

  static addOrUpdateQuestion(disclosureId, payload, forSuperAdmin = false) {
    if (forSuperAdmin) {
      return HttpUtil.post(API_ROUTES.SUPER_ADMIN_GD.UPSERT_QUESTION
        .replace('<disclosureId>', disclosureId), payload
        );
    } else {
      return HttpUtil.post(API_ROUTES.GENERAL_DISCLOSURE.UPSERT_QUESTION
        .replace('<disclosureId>', disclosureId), payload
        );
    }
  }

  static getAllQuestions(category) {
    return HttpUtil.get(API_ROUTES.GENERAL_DISCLOSURE.GET_ALL_QUESTIONS
      .replace(category ? '<category>' : '?category=<category>', category ?? '')
    );
  }

  static saveAnswer(period, payload) {
    return HttpUtil.post(API_ROUTES.GENERAL_DISCLOSURE.SAVE_ANSWER_BULK
      .replace('<period>', period), payload
    );
  }

  /**
   * Same endpoint as saveAnswer; qualitative table search only — encoded period + optional standardType query.
   */
  static saveDisclosureBulkAnswer(period, payload, queryContext = null) {
    let url = API_ROUTES.GENERAL_DISCLOSURE.SAVE_ANSWER_BULK.replace(
      '<period>',
      encodeURIComponent(period)
    );
    if (queryContext != null && typeof queryContext === 'object') {
      const st = queryContext.standardType;
      if (st != null && `${st}`.trim() !== '') {
        url += `?standardType=${encodeURIComponent(String(st).trim())}`;
      }
    }
    return HttpUtil.post(url, payload);
  }

  static deleteAnswer(disclosureId, answerId) {
    return HttpUtil.delete(
      API_ROUTES.GENERAL_DISCLOSURE.DELETE_ANSWER
      .replace("<disclosureId>", disclosureId)
      .replace("<answerId>", answerId)
    );
  }

  static getStandardTypes() {
    return HttpUtil.get(API_ROUTES.GENERAL_DISCLOSURE.GET_STANDARD_TYPES);
  }

  static getQuestionsAndAnswers({ stdType, period, lang='en', periodStart = '', periodEnd = '', buIds = [] }) {
    return HttpUtil.get(API_ROUTES.GENERAL_DISCLOSURE.GET_QUESTIONS_AND_ANSWERS
      .replace('<standardType>', stdType)
      .replace('<period>', period)
      .replace('<lang>', lang)
      .replace(periodStart && periodEnd ? "<periodStart>" : "&periodStart=<periodStart>", periodStart)
      .replace(periodStart && periodEnd ? "<periodEnd>" : "&periodEnd=<periodEnd>", periodEnd)
      .replace(
        buIds?.length > 0 ? "<buIds>" : "&buIds=<buIds>",
        buIds?.length > 0 ? buIds?.toString() : ''
      )
    );
  }

  static getAllQuestionsForStdType(stdType) {
    return HttpUtil.get(API_ROUTES.SUPER_ADMIN_GD.GET_ALL_QUESTIONS_FOR_STD
      .replace('<stdType>', stdType)
    );
  }

  static getPagedQuestionsAndAnswers(stdType, period, pageNumber = 0, pageSize = 10) {
    return HttpUtil.get(API_ROUTES.GENERAL_DISCLOSURE.GET_PAGED_QUESTIONS_AND_ANSWERS
      .replace('<standardType>', stdType)
      .replace('<period>', period)
      .replace("<pageSize>", pageSize)
      .replace("<pageNumber>", pageNumber)
    );
  }

  static getGDQuestionAnswersByCode(period, questionCodes, periodStart = '', periodEnd = '') {
    return HttpUtil.get(
      API_ROUTES.GENERAL_DISCLOSURE.GET_QUESTIONS_AND_ANSWERS_BY_CODE
      .replace("<period>", period)
      .replace("<questionCodes>", questionCodes)
      .replace(periodStart && periodEnd ? "<periodStart>" : "&periodStart=<periodStart>", periodStart)
      .replace(periodStart && periodEnd ? "<periodEnd>" : "&periodEnd=<periodEnd>", periodEnd)
    );
  }

  static deleteAnswers(disclosureId, answerIds) {
    return HttpUtil.delete(
      API_ROUTES.GENERAL_DISCLOSURE.DELETE_ANSWERS_BULK
      .replace("<disclosureId>", disclosureId),
      { data: answerIds }
    );
  }

  static saveRemarkAttachments(period, payload) {
    return HttpUtil.post(
      API_ROUTES.GENERAL_DISCLOSURE.SAVE_REMARK_ATTACHMENTS.replace("<period>", period),
      payload
    );
  }

}
